/**
 * Canvas plumbing for the effect compositor — sizing, layers and cover-fit.
 * Pure helpers, no pipeline state, so the compositor itself stays readable.
 */

/** Never publish above the top simulcast rung — L3 is 1280×720 (LAYER_SPECS). */
const MAX_WIDTH = 1280;
const MAX_HEIGHT = 720;

export interface Size {
  readonly width: number;
  readonly height: number;
}

/** Clamps the capture to the publishable ceiling, keeping the aspect ratio. */
export function publishableSize(width: number, height: number): Size {
  const scale = Math.min(1, MAX_WIDTH / width, MAX_HEIGHT / height);
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

export interface Layer {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D | null;
}

export function createLayer(size: Size, opaque: boolean): Layer {
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  return { canvas, ctx: canvas.getContext('2d', { alpha: !opaque }) };
}

/** Resizes a layer in place. Returns false when nothing changed. */
export function resizeLayer(layer: Layer, size: Size): boolean {
  if (layer.canvas.width === size.width && layer.canvas.height === size.height) return false;
  layer.canvas.width = size.width;
  layer.canvas.height = size.height;
  return true;
}

/** `object-fit: cover`, for a background image of unknown aspect ratio. */
export function drawCover(
  ctx: CanvasRenderingContext2D,
  image: CanvasImageSource,
  size: Size,
): void {
  const sw = Number(Reflect.get(image, 'width')) || size.width;
  const sh = Number(Reflect.get(image, 'height')) || size.height;
  const scale = Math.max(size.width / sw, size.height / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  ctx.drawImage(image, (size.width - dw) / 2, (size.height - dh) / 2, dw, dh);
}
